import { FC, Suspense } from 'react';
import { lazily } from 'react-lazily';
import { createBrowserRouter, Navigate, RouterProvider, useParams } from 'react-router-dom';

import { assertPresence } from '@bonusly/runtime';
import { Permission } from '@bonusly/who-am-i';

import { AdminLegacyPage } from '../components/layout/admin-legacy-page';
import { AdminAppLayout } from '../components/layout/AdminAppLayout';
import { AppRootLayout } from '../components/layout/AppRootLayout';
import { RootLayout } from '../components/layout/root-layout';
import { FlaggedRoute } from '../components/routes/FlaggedRoute';
import { RequireAdmin } from '../components/routes/require-admin';
import { RequirePermission } from '../components/routes/require-permission';
import { RouteError } from '../components/routes/RouteError';
import { MobileUnavailable } from '../components/routes/statuses/MobileUnavailable';
import { PageNotFound } from '../components/routes/statuses/PageNotFound';
import { OAuthApplicationsFeatureFlag } from '../modules/admin-integrations/oauth-applications/lib';
import * as adminRewardsLoaders from '../modules/admin-rewards/loaders';
import { loadRecognitionScoresData } from '../modules/reports/recognition-scores/loaders';
import { loadRewardsHistory, loadRewardsIndex } from '../modules/rewards/loaders';
import { loadAward } from '../queries/awards';
import { loadCelebration, loadCelebrationMetadata, loadInactiveCelebrations } from '../queries/celebrations';
import { loadCustomPropertiesUserGroups, loadUserGroups } from '../queries/groups';
import { loadAdminIncentive } from '../queries/incentives';
import { loadNominationVotingView, loadVotablePeriods } from '../queries/nomination-voting';
import { loadAdminNominationPeriod } from '../queries/nominations';
import { loadSubscriptions } from '../queries/subscriptions';

// Lazily loaded module collections. These are grouped to reduce the number of HTTP requests on app load
const Admin = lazily(() => import('../modules/admin'));
const Awards = lazily(() => import('../modules/awards'));
const Celebrations = lazily(() => import('../modules/celebrations'));
const Growth = lazily(() => import('../modules/growth'));
const Incentives = lazily(() => import('../modules/incentives'));
const Labs = lazily(() => import('../modules/labs'));
const Nominations = lazily(() => import('../modules/nominations'));
const Profile = lazily(() => import('../modules/profile'));
const Recognition = lazily(() => import('../modules/recognition'));
const Reports = lazily(() => import('../modules/reports'));
const Rewards = lazily(() => import('../modules/rewards'));
const SuperAdmin = lazily(() => import('../modules/super-admin'));
const AdminKyb = lazily(() => import('../modules/admin-kyb'));
const Meet = lazily(() => import('../modules/meet'));
const Spotlight = lazily(() => import('../modules/spotlight'));
const AdminIntegrations = lazily(() => import('../modules/admin-integrations/workday-anytime-feedback'));
const AdminOAuthApplications = lazily(() => import('../modules/admin-integrations/oauth-applications'));
const AdminPersonalAccessTokens = lazily(() => import('../modules/admin-integrations/personal-access-tokens'));
const AdminScim = lazily(() => import('../modules/admin-integrations/scim'));
const AdminDigitalSignage = lazily(() => import('../modules/admin-integrations/digital-signage'));
const AdminUserGroupQueries = lazily(() => import('../modules/admin-user-group-queries'));
const UserSettings = lazily(() => import('../modules/user-settings'));
const BizyEarlyAccess = lazily(() => import('../components/bizy-early-access'));
const TrialWhatsNext = lazily(() => import('../modules/trial-whats-next'));

// Widget components - lazy loaded to avoid bloating main bundle
const { WidgetLayout } = lazily(() => import('../components/layout/widget-layout'));
const { WidgetPage } = lazily(() => import('../components/widget/widget-page'));

const RewardDialogRedirect: FC = () => {
	const { rewardId } = useParams();

	return <Navigate to={`/rewards?dialog=reward&rewardId=${rewardId ?? ''}`} replace />;
};

const router = createBrowserRouter([
	{
		path: '',
		element: <RootLayout />,
		errorElement: <RouteError />,
		children: [
			{
				path: 'admin',
				element: <AdminAppLayout />,
				children: [
					{
						path: 'company/*',
						element: <Admin.Company />,
					},
					{
						path: 'dashboard/*',
						element: (
							<RequireAdmin require={['reports', 'global', 'finance']} statusCode={403}>
								<Admin.Dashboard />
							</RequireAdmin>
						),
					},
					{
						path: 'growth/*',
						element: <Admin.Growth />,
					},
					{
						path: 'users/manage-team-requests',
						element: (
							<RequireAdmin require={['global']} statusCode={403}>
								<RequirePermission permission={Permission.CanManageTeamRequests} statusCode={404}>
									<Admin.TeamSetup />
								</RequirePermission>
							</RequireAdmin>
						),
					},
					{
						path: 'users/teams-and-groups',
						element: <AdminUserGroupQueries.UserGroupQueriesRoot />,
						children: [
							{
								path: '',
								element: <AdminUserGroupQueries.UserGroupQueriesIndex />,
							},
							{
								path: 'new',
								element: (
									<FlaggedRoute variant="custom-user-groups">
										<AdminUserGroupQueries.CreateUserGroupQuery />
									</FlaggedRoute>
								),
								loader: () => loadCustomPropertiesUserGroups(),
							},
							{
								path: ':id/edit',
								element: (
									<FlaggedRoute variant="custom-user-groups">
										<AdminUserGroupQueries.EditUserGroupQuery />
									</FlaggedRoute>
								),
								loader: () => loadCustomPropertiesUserGroups(),
							},
						],
					},
					{
						path: 'incentives/*',
						element: (
							<RequireAdmin require={['reports', 'global', 'awards']} statusCode={403}>
								<RequirePermission permission={Permission.CanViewExpandedReporting} statusCode={403}>
									<Incentives.IncentivesSummary />
								</RequirePermission>
							</RequireAdmin>
						),
						children: [
							{
								path: 'summary',
								element: <Incentives.IncentivesSummary />,
							},
						],
					},
					{
						path: 'subscriptions',
						element: (
							<RequireAdmin require={['reports', 'global', 'finance', 'awards', 'rewards']} statusCode={403}>
								<Admin.ReportSubscriptions />
							</RequireAdmin>
						),
						loader: () => loadSubscriptions(),
					},
					{
						path: 'reports/collaboration',
						element: (
							<RequireAdmin require={['reports', 'global']} statusCode={403}>
								<Reports.CollaborationReport />
							</RequireAdmin>
						),
					},
					{
						path: 'reports/participation',
						element: (
							<RequireAdmin require={['reports', 'global']} statusCode={403}>
								<RequirePermission permission={Permission.CanViewExpandedReporting} fallback={<Reports.ReportUpgradeState report="participation" />}>
									<Reports.ParticipationReport />
								</RequirePermission>
							</RequireAdmin>
						),
						children: [
							{
								path: 'giving-and-receiving',
								element: <Reports.ParticipationReport />,
							},
							{
								path: 'managers-and-teams',
								element: <Reports.ParticipationReport />,
							},
							{
								path: 'compare',
								element: <Reports.ParticipationReport />,
							},
						],
					},
					{
						path: 'programs/awards',
						element: <Awards.AwardsRoot />,
						children: [
							{
								path: '',
								element: <Awards.AwardsIndex />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
							{
								path: 'new',
								element: <Awards.CreateAward />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
							{
								path: ':id/edit',
								element: <Awards.EditAward />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return Promise.all([loadAward(params.id), loadCustomPropertiesUserGroups()]);
								},
							},
							{
								path: 'archived',
								element: <Awards.AwardsArchivedIndex />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
						],
					},
					{
						path: 'programs/celebrations',
						element: <Celebrations.CelebrationsRoot />,
						children: [
							{
								path: '',
								element: <Celebrations.CelebrationsIndex />,
								loader: () => {
									return loadInactiveCelebrations();
								},
							},
							{
								path: 'new/:type',
								element: <Celebrations.CreateCelebration />,
								loader: ({ params }) => {
									assertPresence(params.type);
									return Promise.all([loadUserGroups(), loadCelebrationMetadata({ celebration_type: params.type })]);
								},
							},
							{
								path: ':id/edit',
								element: <Celebrations.EditCelebration />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return Promise.all([loadCelebration(params.id), loadUserGroups(), loadCelebrationMetadata({ celebration_id: params.id })]);
								},
							},
						],
					},
					{
						path: 'programs/nominations',
						element: <Nominations.NominationsRoot />,
						children: [
							{
								path: '',
								element: <Nominations.NominationPeriodsIndex />,
							},
							{
								path: 'new',
								element: <Nominations.CreateNominationPeriod />,
								loader: () => Promise.all([loadUserGroups(), loadCustomPropertiesUserGroups()]),
							},
							{
								path: 'archived',
								element: <Nominations.NominationPeriodsArchivedIndex />,
							},
							{
								path: ':id',
								element: <Nominations.AdminNominationPeriod />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return loadAdminNominationPeriod(params.id);
								},
							},
							{
								path: ':id/edit',
								element: <Nominations.EditNominationPeriod />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return Promise.all([loadAdminNominationPeriod(params.id), loadUserGroups(), loadCustomPropertiesUserGroups()]);
								},
							},
						],
					},
					{
						path: 'programs/incentives',
						element: <Incentives.IncentivesRoot />,
						children: [
							{
								path: '',
								element: <Incentives.IncentivesIndex />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
							{
								path: 'new',
								element: <Incentives.CreateIncentive />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
							{
								path: ':id/edit',
								element: <Incentives.EditIncentive />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return Promise.all([loadAdminIncentive(params.id), loadCustomPropertiesUserGroups()]);
								},
							},
							{
								path: 'archived',
								element: <Incentives.IncentivesArchivedIndex />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
						],
					},
					{
						path: 'programs/*',
						element: <Admin.Programs />,
					},
					{
						// Admin adjudication queue for reported recognition posts —
						// replaces the legacy iframe at /admin/r/company/bonus_reports.
						path: 'recognition/reported-posts',
						element: (
							<RequireAdmin require={['global']} statusCode={403}>
								<Admin.ReportedPostsPage />
							</RequireAdmin>
						),
					},
					{
						path: 'reports/recognition_scores',
						element: (
							<RequireAdmin require={['reports', 'global']} statusCode={403}>
								<Reports.RecognitionScores />
							</RequireAdmin>
						),
						loader: loadRecognitionScoresData,
					},
					{
						path: 'reports/pulse-results',
						element: (
							<RequireAdmin require={['reports', 'global']} statusCode={403}>
								<Reports.CheckInsResults />
							</RequireAdmin>
						),
					},
					{
						path: 'reports/recognition-activity',
						element: (
							<RequireAdmin require={['global', 'awards', 'reports']} statusCode={403}>
								<Reports.RecognitionActivityReport />
							</RequireAdmin>
						),
					},
					{
						path: 'reports/points-activity',
						element: (
							<RequireAdmin require={['finance', 'reports', 'global']} statusCode={403}>
								<RequirePermission permission={Permission.CanViewExpandedReporting} fallback={<Reports.ReportUpgradeState report="points-activity" />}>
									<Reports.PointsActivityReport />
								</RequirePermission>
							</RequireAdmin>
						),
						children: [
							{
								path: 'summary',
								element: <Reports.PointsActivityReport />,
							},
							{
								path: 'programs',
								element: <Reports.PointsActivityReport />,
							},
							{
								path: 'reward-balance',
								element: <Reports.PointsActivityReport />,
							},
						],
					},
					{
						path: 'rewards',
						element: <Admin.AdminRewardsRoot />,
						children: [
							{
								path: 'catalog',
								element: <Admin.RewardsCatalogIndex />,
								loader: adminRewardsLoaders.loadIndex,
							},
							{
								path: 'collections/exclusive',
								element: <Admin.ExclusiveItems />,
								loader: adminRewardsLoaders.loadExclusiveItems,
							},
							{
								path: 'collections/swag',
								element: <Admin.SwagItems />,
								loader: adminRewardsLoaders.loadSwagItems,
							},
							{
								path: 'collections/:id',
								element: <Admin.RewardsCollectionDetail />,
								loader: adminRewardsLoaders.loadCollectionDetail,
							},
							{
								path: 'exclusive/new',
								element: <Admin.CreateExclusiveReward />,
							},
							{
								path: 'exclusive/:id/edit',
								element: <Admin.EditExclusiveReward />,
								loader: adminRewardsLoaders.loadEditExclusiveReward,
							},
							{
								path: 'items',
								element: <Admin.RewardsManageItems />,
								loader: adminRewardsLoaders.loadItems,
							},
							{
								path: 'settings',
								element: <Admin.RewardsSettings />,
								loader: adminRewardsLoaders.loadSettings,
							},
						],
					},

					{
						path: '*',
						element: <AdminLegacyPage />,
					},
				],
			},
			{
				path: 'company/rewards',
				element: <AdminAppLayout />,
				children: [
					{
						path: 'totals_report',
						element: (
							<RequireAdmin require={['finance', 'reports', 'global', 'rewards']} statusCode={403}>
								<Reports.RewardsCostSummaryReport />
							</RequireAdmin>
						),
					},
					{
						path: 'redemptions',
						element: (
							<RequireAdmin require={['finance', 'reports', 'global', 'rewards']} statusCode={403}>
								<Reports.RewardsRedemptionsReport />
							</RequireAdmin>
						),
					},
				],
			},
			{
				path: 'company/integrations',
				element: <AdminAppLayout />,
				children: [
					{
						path: 'workday-anytime-feedback',
						element: (
							<FlaggedRoute variant="workday-anytime-feedback">
								<AdminIntegrations.WorkdayAnytimeFeedback />
							</FlaggedRoute>
						),
					},
					{
						path: 'workday-anytime-feedback/:id',
						element: (
							<FlaggedRoute variant="workday-anytime-feedback">
								<AdminIntegrations.WorkdayAnytimeFeedback />
							</FlaggedRoute>
						),
					},
					{
						path: 'oauth-applications',
						element: (
							<FlaggedRoute variant={OAuthApplicationsFeatureFlag}>
								<AdminOAuthApplications.OAuthApplicationsIndex />
							</FlaggedRoute>
						),
					},
					{
						path: 'oauth-applications/new',
						element: (
							<FlaggedRoute variant={OAuthApplicationsFeatureFlag}>
								<AdminOAuthApplications.OAuthApplicationsCreate />
							</FlaggedRoute>
						),
					},
					{
						path: 'oauth-applications/:id/edit',
						element: (
							<FlaggedRoute variant={OAuthApplicationsFeatureFlag}>
								<AdminOAuthApplications.OAuthApplicationsEdit />
							</FlaggedRoute>
						),
					},
					{
						path: 'personal-access-tokens',
						element: (
							<RequireAdmin require={['global', 'tech']} statusCode={403}>
								<AdminPersonalAccessTokens.PersonalAccessTokensIndex />
							</RequireAdmin>
						),
					},
					{
						path: 'personal-access-tokens/new',
						element: (
							<RequireAdmin require={['global', 'tech']} statusCode={403}>
								<AdminPersonalAccessTokens.PersonalAccessTokensCreate />
							</RequireAdmin>
						),
					},
					{
						path: 'scim',
						element: (
							<RequireAdmin require={['global', 'tech']} statusCode={403}>
								<AdminScim.ScimPage />
							</RequireAdmin>
						),
					},
					{
						path: 'digital-signage',
						element: (
							<RequireAdmin require={['global', 'tech']} statusCode={403}>
								<AdminDigitalSignage.DigitalSignagePage />
							</RequireAdmin>
						),
					},
				],
			},
			{
				path: 'super_admin',
				element: <SuperAdmin.SuperAdminAppLayout />,
				children: [
					{
						path: 'kvms/recognition/giving-rate',
						element: <SuperAdmin.RecognitionGivingRate />,
					},
					{
						path: 'kvms/rewards/redemptions-per-user',
						element: <SuperAdmin.RedemptionsPerUser />,
					},
					{
						path: 'kvms/one-on-one',
						element: <SuperAdmin.OneOnOneKvms />,
					},
					{
						path: 'kvms/pulse-checks',
						element: <SuperAdmin.PulseChecksKvms />,
					},
					{
						path: 'kvms/insights',
						element: <SuperAdmin.InsightsDashboard />,
					},
					{
						path: 'kvms/one-on-one-activation',
						element: <SuperAdmin.OneOnOneActivationDashboard />,
					},
					{
						path: 'kvms/transcript-metrics',
						element: <SuperAdmin.TranscriptMetricsDashboard />,
					},
					{
						path: 'kvms/notification-metrics',
						element: <SuperAdmin.NotificationMetricsDashboard />,
					},
					{
						path: 'kvms/trial-conversion',
						element: <SuperAdmin.TrialConversionDashboard />,
					},
					{
						path: 'kvms/app-performance',
						element: <SuperAdmin.AppPerformanceDashboard />,
					},
					{
						path: 'kvms/goals-adoption',
						element: <SuperAdmin.GoalsAdoptionDashboard />,
					},
					{
						path: 'kvms/advice-adoption',
						element: <SuperAdmin.AdviceAdoptionDashboard />,
					},
					{
						path: 'kvms/one-on-one-adoption',
						element: <SuperAdmin.OneOnOneAdoptionDashboard />,
					},
					{
						path: 'kvms/bizy/draft-assist-outcomes',
						element: <SuperAdmin.DraftAssistOutcomeMetrics />,
					},
					{
						path: 'kyb',
						element: <AdminKyb.KybPage />,
					},
					{
						path: 'bizy/conversations',
						element: <SuperAdmin.BizyConversations />,
					},
					{
						path: 'bizy/dreaming',
						element: <SuperAdmin.BizyDreaming />,
					},
					{
						path: 'bizy/conversations/:conversationId',
						element: <SuperAdmin.BizyConversationDetail />,
					},
					{
						path: 'one_on_ones/conversation_suggestions',
						element: <SuperAdmin.ConversationSuggestions />,
					},
					{
						path: 'one_on_ones/conversation_suggestions/:suggestionId',
						element: <SuperAdmin.ConversationSuggestionDetail />,
					},
					{
						path: 'bizy/feature-requests',
						element: <SuperAdmin.BizyFeatureRequests />,
					},
					{
						path: 'bizy/early-access-requests',
						element: <SuperAdmin.BizyEarlyAccessRequests />,
					},
					{
						path: 'bizy/feedback',
						element: <SuperAdmin.BizyFeedback />,
					},
					{
						path: 'bizy/trends',
						element: <SuperAdmin.BizyTrends />,
					},
					{
						path: 'bizy/overview',
						element: <SuperAdmin.BizyOverview />,
					},
					{
						path: 'bizy/topics',
						element: <SuperAdmin.BizyTopicsExplorer />,
					},
					{
						path: 'bizy/training_conversations',
						element: <SuperAdmin.BizyTrainingConversations />,
					},
					{
						path: 'bizy/triggers',
						element: <SuperAdmin.BizyTriggers />,
					},
					{
						path: 'oauth-applications',
						element: <SuperAdmin.SuperAdminOAuthApplicationsIndex />,
					},
					{
						path: 'oauth-applications/new',
						element: <SuperAdmin.SuperAdminOAuthApplicationsCreate />,
					},
					{
						path: 'oauth-applications/:id/edit',
						element: <SuperAdmin.SuperAdminOAuthApplicationsEdit />,
					},
					{
						path: 'public-api/metrics',
						element: <SuperAdmin.PublicApiMetrics />,
					},
					{
						path: 'public-api/rate-limits',
						element: <SuperAdmin.PublicApiRateLimits />,
					},
					{
						path: 'companies/usage_events',
						element: <SuperAdmin.UsageEventsDashboard />,
					},
					{
						path: 'companies/usage_events/:companyId',
						element: <SuperAdmin.UsageEventsDashboard />,
					},
					{ path: '*', element: <AdminLegacyPage /> },
				],
			},

			// Print-friendly and shared pages without layout
			{
				path: 'p',
				children: [
					{
						path: 'recap-review/*',
						element: (
							<Suspense>
								<Growth.RecapReviewPrintView />
							</Suspense>
						),
					},
				],
			},

			// Widget page for iframe embedding without layout
			{
				path: 'widget',
				element: <WidgetLayout />,
				children: [
					{
						path: '',
						element: <WidgetPage />,
					},
				],
			},

			// Shareable routes without layout
			{
				path: 'share',
				children: [
					{
						path: 'nominations/:id',
						element: (
							<Suspense>
								<Nominations.PublicNominationPeriod />
							</Suspense>
						),
						loader: ({ params }) => {
							assertPresence(params.id);
							return loadAdminNominationPeriod(params.id);
						},
					},
					{
						path: 'incentives/approval-action',
						element: (
							<Suspense>
								<Incentives.ApprovalAction />
							</Suspense>
						),
					},
				],
			},

			// Voter-facing nomination voting page. Lives outside /admin and /share
			// because eligibility is gated server-side and the page uses NavlessLayout.
			// Loader errors (403/404/etc.) bubble to the root RouteError, like the
			// sibling /share routes.
			{
				path: 'vote',
				children: [
					{
						path: 'nominations',
						children: [
							{
								index: true,
								element: (
									<Suspense>
										<Nominations.NominationVotingIndexPage />
									</Suspense>
								),
								loader: () => loadVotablePeriods(),
							},
							{
								path: ':id',
								element: (
									<Suspense>
										<Nominations.NominationVotingPage />
									</Suspense>
								),
								loader: ({ params }) => {
									assertPresence(params.id);
									return loadNominationVotingView(params.id);
								},
							},
						],
					},
				],
			},

			{
				path: 'spotlight/employee/:spotlightId',
				element: <Spotlight.UserSpotlightViewerPage />,
			},

			// Legacy route - redirect to new path for backward compatibility
			{
				path: 'spotlight/:spotlightId',
				element: <Spotlight.LegacySpotlightRedirect />,
			},

			// Redirect auth routes to home - these are handled by Rails, not the React app
			// Users who navigate directly to these URLs on the React app should be redirected
			{ path: 'users/sign_in', element: <Navigate to="/" replace /> },
			{ path: 'users/sign_in_start', element: <Navigate to="/" replace /> },
			{ path: 'sign-in', element: <Navigate to="/" replace /> },

			// The rest of the app uses the AppRootLayout
			{
				path: '',
				element: <AppRootLayout />,
				children: [
					{
						path: '',
						element: <Recognition.BonusFeed />,
						// Originally this was set to three different paths that each would show `Recognition.BonusFeed`.
						// What this meant, however, is that if the path changed it would totally unmount and then remount
						// the component tree. So, if you were on the home page and navigated to /invite from a button (which
						// should still show the home page) you'd see a flash, then the home page would load, followed by the
						// invite modal. This causes issues when multiple modals are already open, however, because handlers
						// and references added to the screen seem to be lost. So, what this does is just treat the three paths
						// as child routes but don't actually render anything for them. This allows the BonusFeed component to
						// remain mounted and visible while the user navigates between the different paths.
						children: [
							{
								path: '',
								element: null,
							},
							{
								path: 'invite',
								element: null,
							},
							{
								path: 'bonuses/:id',
								element: null,
							},
						],
					},
					{
						path: 'rewards/history',
						element: <Rewards.RewardsHistory />,
						loader: loadRewardsHistory,
					},
					{
						path: 'rewards',
						element: <Rewards.RewardsIndex />,
						loader: loadRewardsIndex,
						shouldRevalidate: ({ currentUrl, nextUrl }) => {
							if (!currentUrl || !nextUrl) {
								return true;
							}

							const currentPath = new URL(currentUrl).pathname;
							const nextPath = new URL(nextUrl).pathname;
							return currentPath !== nextPath;
						},
					},
					{
						path: 'rewards/:rewardId',
						element: <RewardDialogRedirect />,
					},
					{
						path: 'bookmark',
						element: <Recognition.Bookmarks />,
					},
					{
						path: 'meet/*',
						element: <Meet.Meet />,
					},
					{
						path: 'growth/*',
						element: <Growth.Growth />,
					},
					{
						path: 'incentives/*',
						element: <Incentives.IncentiveSelect />,
						children: [
							{
								path: ':id',
								element: <Incentives.IncentiveDetails />,
							},
						],
					},
					{
						path: 'team/*',
						element: <Growth.TeamManagement />,
					},
					{
						path: 'company/users/:userId',
						element: <Profile.FlaggedProfilePage />,
					},
					{
						path: 'user/settings/*',
						element: <UserSettings.App />,
					},
					{
						path: 'user-settings',
						element: <Navigate to="/user/settings" replace />,
					},
					{
						path: 'users/edit',
						element: <Navigate to="/user/settings" replace />,
					},
					{
						path: 'mobile-unavailable',
						element: <MobileUnavailable />,
					},
					{
						path: 'labs',
						element: (
							<FlaggedRoute variant="bonusly-labs">
								<Labs.App />
							</FlaggedRoute>
						),
					},
					{
						// Bizy early-access opt-in landing page — the early-access
						// promo's CTA links here. Auto-opts the logged-in viewer in
						// on visit (BIZY-331).
						path: 'bizy/early-access',
						element: <BizyEarlyAccess.BizyEarlyAccessPage />,
					},
					{
						// End-of-trial "what's next?" recommendation (RR-75). A real
						// linkable route; the same view also auto-presents on login via
						// <TrialWhatsNextAutoPresent> in the global app shell. Renders
						// nothing when the endpoint 404s (company out of scope).
						path: 'trial/whats-next',
						element: <TrialWhatsNext.TrialWhatsNextPage />,
					},
					{ path: '*', element: <PageNotFound /> },
				],
			},
			// 404 page for the rest of the app
			{ path: '*', element: <PageNotFound /> },
		],
	},
]);

export const AppRouter: FC = () => <RouterProvider router={router} />;
